import { useEffect, useState } from 'react'
import { getDocument } from '@/lib/firestore-crud'
import { Activity } from 'lucide-react'

const AppleIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 384 512" fill="currentColor">
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
  </svg>
)

const GooglePlayIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 512 512" fill="currentColor">
    <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z"/>
  </svg>
)

const DEFAULT_LINKS = {
  android: 'https://play.google.com/store/apps/details?id=com.parlentworld.app',
  ios: 'https://apps.apple.com/id/app/parlent/id6760335221',
}

type Platform = keyof typeof DEFAULT_LINKS

/**
 * Kembalikan platform toko yang cocok, atau null bila tidak yakin.
 *
 * null berarti "biarkan pengguna memilih sendiri" -- itulah perilaku fallback
 * untuk desktop, tablet Android, dan user agent yang tidak dikenali.
 */
function detectPlatform(): Platform | null {
  if (typeof navigator === 'undefined') return null
  const ua = navigator.userAgent || ''
  if (/android/i.test(ua)) return 'android'
  if (/iPad|iPhone|iPod/.test(ua)) return 'ios'
  // iPadOS 13+ menyamar sebagai Mac di user agent; satu-satunya pembeda yang
  // andal adalah adanya layar sentuh.
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) return 'ios'
  return null
}

/**
 * Ubah URL web toko menjadi deep link yang HANYA bisa ditangani aplikasi toko.
 *
 * https://play.google.com/... yang dibuka lewat navigasi JavaScript sering
 * tidak memicu App Link di Chrome Android, sehingga yang terbuka justru versi
 * webnya. Skema di bawah tidak punya penanganan web sama sekali, jadi sistem
 * wajib menyerahkannya ke aplikasi toko.
 *
 * Diturunkan dari URL yang tersimpan, bukan dari konstanta, supaya override
 * lewat appSettings tetap dihormati. Mengembalikan null bila tidak bisa
 * diurai -- pemanggil lalu memakai URL web apa adanya.
 */
function toNativeStoreUrl(platform: Platform, httpsUrl: string): string | null {
  try {
    const url = new URL(httpsUrl)
    if (platform === 'android') {
      const pkg = url.searchParams.get('id')
      if (!pkg) return null
      // intent:// adalah cara resmi Chrome Android: bila Play Store tidak ada,
      // Chrome sendiri yang memindahkan pengguna ke browser_fallback_url.
      return (
        'intent://details?id=' + encodeURIComponent(pkg) +
        '#Intent;scheme=market;package=com.android.vending' +
        ';S.browser_fallback_url=' + encodeURIComponent(httpsUrl) +
        ';end'
      )
    }
    // apps.apple.com/<bahasa>/app/<slug>/id<digit>
    const match = url.pathname.match(/\/id(\d+)/)
    return match ? 'itms-apps://apps.apple.com/app/id' + match[1] : null
  } catch {
    return null
  }
}

export function QrDownloadPublicPage() {
  const [links, setLinks] = useState(DEFAULT_LINKS)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    async function loadConfig() {
      // Selalu mulai dari tautan bawaan. Versi sebelumnya hanya memanggil
      // setLinks() bila dokumennya ADA, sehingga saat appSettings/current
      // hilang atau pembacaannya gagal, kedua tombol jadi href kosong yang
      // cuma memuat ulang halaman ini.
      let resolved = DEFAULT_LINKS
      try {
        const doc = await getDocument<any>('appSettings', 'current')
        resolved = {
          android: doc?.android?.storeUrl || DEFAULT_LINKS.android,
          ios: doc?.ios?.storeUrl || DEFAULT_LINKS.ios,
        }
      } catch (e) {
        console.error('Gagal memuat store url, memakai tautan bawaan', e)
      }
      if (cancelled) return
      setLinks(resolved)

      // ?stay=1 mematikan pengalihan, supaya halaman ini tetap bisa diperiksa
      // dari ponsel saat menguji QR-nya.
      const stay = new URLSearchParams(window.location.search).has('stay')
      const target = stay ? null : detectPlatform()

      if (target) {
        const webUrl = resolved[target]
        const nativeUrl = toNativeStoreUrl(target, webUrl)

        if (!nativeUrl) {
          // replace(), bukan assign(): tombol Back membawa pengguna kembali ke
          // halaman sebelum QR, bukan memantul ke sini lalu dialihkan lagi.
          window.location.replace(webUrl)
          return
        }

        // Bila aplikasi toko benar-benar terbuka, halaman ini disembunyikan.
        // Itu sinyal untuk membatalkan fallback -- tanpa ini, pengguna yang
        // kembali dari Play Store akan langsung dilempar ke versi web.
        const toWeb = window.setTimeout(() => {
          if (!cancelled) window.location.replace(webUrl)
        }, 1500)
        const onHide = () => {
          if (document.visibilityState === 'hidden') window.clearTimeout(toWeb)
        }
        document.addEventListener('visibilitychange', onHide)

        window.location.href = nativeUrl

        // Lapis terakhir: bila tidak ada yang terjadi sama sekali, tampilkan
        // halaman pilihan manual daripada membiarkan spinner berputar abadi.
        window.setTimeout(() => {
          if (!cancelled) setLoading(false)
        }, 3000)
        return
      }

      setLoading(false)
    }

    loadConfig()
    return () => {
      cancelled = true
    }
  }, [])

  if (loading) {
    return (
      <div className="min-h-[100dvh] h-full bg-[#0a0a0a] flex items-center justify-center p-4">
        <Activity className="h-8 w-8 text-orange-500 animate-pulse" />
      </div>
    )
  }

  return (
    <div className="min-h-[100dvh] h-full bg-[#0a0a0a] flex items-center justify-center p-4 font-sans relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-72 h-72 bg-rose-500/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 w-full max-w-sm">
        <div className="bg-[#141414]/80 backdrop-blur-xl border border-stone-800 rounded-[32px] p-8 shadow-2xl text-center">
          <div className="w-24 h-24 mx-auto rounded-[24px] bg-gradient-to-br from-orange-500 to-rose-500 flex items-center justify-center mb-6 shadow-xl shadow-orange-500/20 overflow-hidden outline outline-1 outline-white/10 outline-offset-[-1px]">
            <img src="/logo.jpeg" alt="Parlent Launcher Icon" className="w-full h-full object-cover" />
          </div>

          <h1 className="text-xl font-bold text-white mb-2 tracking-tight">Download Parlent</h1>
          <p className="text-sm text-stone-400 mb-8 leading-relaxed">
            Get the companion app to sync your data, customize watch faces, and track your health.
          </p>

          <div className="space-y-4">
            <a
              href={links.android}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 bg-stone-900 border border-stone-800 hover:border-emerald-500/50 hover:bg-emerald-500/5 transition-all p-4 rounded-2xl group"
            >
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center group-hover:bg-emerald-500/20 transition-colors">
                <GooglePlayIcon className="h-6 w-6 text-emerald-400" />
              </div>
              <div className="text-left">
                <p className="text-[11px] uppercase tracking-wider text-stone-500 font-bold mb-0.5">Get it on</p>
                <p className="text-base font-bold text-white tracking-tight">Google Play</p>
              </div>
            </a>

            <a
              href={links.ios}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 bg-stone-900 border border-stone-800 hover:border-blue-500/50 hover:bg-blue-500/5 transition-all p-4 rounded-2xl group"
            >
              <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center group-hover:bg-blue-500/20 transition-colors">
                <AppleIcon className="h-6 w-6 text-blue-400" />
              </div>
              <div className="text-left">
                <p className="text-[11px] uppercase tracking-wider text-stone-500 font-bold mb-0.5">Available on</p>
                <p className="text-base font-bold text-white tracking-tight">App Store</p>
              </div>
            </a>
          </div>
        </div>
        
        <p className="text-center text-xs text-stone-600 mt-8">
          © 2026 Parlent World. All rights reserved.
        </p>
      </div>
    </div>
  )
}
